import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Bell, Trash2, Loader2, Plus, ChevronDown, ChevronRight, ClipboardList, User, FileText, Lightbulb, ArrowRight } from 'lucide-react';
import notificationService from '../../services/notificationService';
import toast from 'react-hot-toast';
import { Link } from 'react-router-dom';

const SimpleNotificationPreferences = ({ embedded = false }) => {
  const [subscriptions, setSubscriptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedGroups, setExpandedGroups] = useState({});

  useEffect(() => {
    fetchSubscriptions();
  }, []);

  const fetchSubscriptions = async () => {
    try {
      setLoading(true);
      const subs = await notificationService.getSubscriptions();
      // Only show enabled subscriptions
      const enabledSubs = subs.filter(sub => sub.enabled);
      setSubscriptions(enabledSubs);
      
      // Expand all groups by default
      const groups = groupSubscriptions(enabledSubs);
      const expanded = {};
      Object.keys(groups).forEach(key => {
        expanded[key] = true;
      });
      setExpandedGroups(expanded);
    } catch (error) {
      console.error('Failed to fetch subscriptions:', error);
      toast.error('Failed to load notification preferences');
    } finally {
      setLoading(false);
    }
  };

  const groupSubscriptions = (subs) => {
    const grouped = {};
    
    subs.forEach(sub => {
      let groupKey;
      let groupLabel;
      
      if (sub.study_id && sub.site_id) {
        groupKey = `study_${sub.study_id}_site_${sub.site_id}`;
        groupLabel = `${sub.study_name || 'Study'} - ${sub.site_name || 'Site'}`;
      } else if (sub.study_id) {
        groupKey = `study_${sub.study_id}`;
        groupLabel = sub.study_name || `Study ${sub.study_id}`;
      } else if (sub.site_id) {
        groupKey = `site_${sub.site_id}`;
        groupLabel = sub.site_name || `Site ${sub.site_id}`;
      } else {
        groupKey = 'global';
        groupLabel = 'All Studies';
      }
      
      if (!grouped[groupKey]) {
        grouped[groupKey] = {
          label: groupLabel,
          studyId: sub.study_external_id,
          siteId: sub.site_external_id,
          subscriptions: []
        };
      }
      
      grouped[groupKey].subscriptions.push(sub);
    });
    
    return grouped;
  };

  const toggleSubscription = async (subscriptionId, currentState) => {
    // Optimistic update
    setSubscriptions(prev => 
      prev.map(sub => 
        sub.id === subscriptionId 
          ? { ...sub, enabled: !currentState, in_app_enabled: !currentState }
          : sub
      )
    );

    try {
      await notificationService.updateSubscription(subscriptionId, {
        enabled: !currentState,
        in_app_enabled: !currentState
      });
      toast.success(currentState ? 'Notification disabled' : 'Notification enabled');
    } catch (error) {
      console.error('Failed to toggle subscription:', error);
      // Revert on error
      setSubscriptions(prev => 
        prev.map(sub => 
          sub.id === subscriptionId 
            ? { ...sub, enabled: currentState, in_app_enabled: currentState }
            : sub
        )
      );
      toast.error('Failed to update notification');
    }
  };

  const deleteSubscription = async (subscriptionId) => {
    const subToDelete = subscriptions.find(s => s.id === subscriptionId);
    
    // Optimistic delete
    setSubscriptions(prev => prev.filter(sub => sub.id !== subscriptionId));
    
    // Show toast with undo option
    const toastId = toast.success(
      (t) => (
        <div className="flex items-center justify-between gap-3">
          <span>Notification removed</span>
          <button
            onClick={() => {
              // Restore the subscription
              setSubscriptions(prev => [...prev, subToDelete]);
              toast.dismiss(t.id);
              toast.success('Notification restored');
            }}
            className="rounded-md px-2.5 py-1 text-sm font-medium text-primary-700 transition-colors hover:bg-primary-50 dark:text-primary-400 dark:hover:bg-primary-500/10"
          >
            Undo
          </button>
        </div>
      ),
      { duration: 5000 }
    );
    
    try {
      await notificationService.deleteSubscription(subscriptionId);
    } catch (error) {
      console.error('Failed to delete subscription:', error);
      // Restore on error
      setSubscriptions(prev => [...prev, subToDelete]);
      toast.dismiss(toastId);
      toast.error('Failed to remove notification');
    }
  };

  const toggleGroup = (groupKey) => {
    setExpandedGroups(prev => ({
      ...prev,
      [groupKey]: !prev[groupKey]
    }));
  };

  const getEventDisplayName = (eventKey) => {
    const names = {
      'survey.missed': 'Missed Surveys',
      'survey.completed': 'Survey Completions',
      'participant.created': 'New Participants',
      'participant.activated': 'Participant Activations',
      'participant.deactivated': 'Participant Deactivations',
      'dcq.created': 'New Data Queries',
      'dcq.updated': 'Data Query Updates'
    };
    return names[eventKey] || eventKey.replace(/\./g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  const getEventIcon = (eventKey) => {
    if (eventKey.startsWith('survey.')) return ClipboardList;
    if (eventKey.startsWith('participant.')) return User;
    if (eventKey.startsWith('dcq.')) return FileText;
    return Bell;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-12">
        <Loader2 className="h-7 w-7 animate-spin text-primary-600 dark:text-primary-400" />
      </div>
    );
  }

  const groupedSubs = groupSubscriptions(subscriptions);
  const hasSubscriptions = Object.keys(groupedSubs).length > 0;

  return (
    <div className={embedded ? "" : "max-w-4xl mx-auto"}>
      <div className={embedded ? "" : "card overflow-hidden"}>
        {/* Header */}
        <div className={embedded ? "mb-6" : "border-b border-gray-200 bg-gray-50 px-6 py-4 dark:border-gray-700 dark:bg-gray-800"}>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              {!embedded && (
                <div className="rounded-lg bg-primary-50 p-2 dark:bg-primary-900/20">
                  <Bell className="h-5 w-5 text-primary-600 dark:text-primary-400" />
                </div>
              )}
              <div>
                <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Notification Preferences</h2>
                <p className="mt-0.5 text-sm text-gray-500 dark:text-gray-400">
                  {hasSubscriptions 
                    ? `${subscriptions.length} active notification${subscriptions.length === 1 ? '' : 's'}`
                    : 'Manage your notification settings'}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className={embedded ? "" : "p-6"}>
          {!hasSubscriptions ? (
            <div className="py-8 text-center">
              <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-gray-100 dark:bg-white/[0.06]">
                <Bell className="h-6 w-6 text-gray-400 dark:text-gray-500" strokeWidth={1.75} />
              </div>
              <h3 className="mb-1.5 text-base font-semibold text-gray-900 dark:text-gray-100">No Active Notifications</h3>
              <p className="mx-auto mb-5 max-w-md text-sm text-gray-600 dark:text-gray-400">
                Set up notifications for your studies to stay informed about important events.
              </p>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Visit a <Link to="/studies" className="link">study page</Link> and 
                go to the Notifications tab to enable alerts.
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              {Object.entries(groupedSubs).map(([groupKey, group]) => (
                <div key={groupKey} className="overflow-hidden rounded-xl border border-gray-200 dark:border-gray-700">
                  {/* Group Header */}
                  <button
                    onClick={() => toggleGroup(groupKey)}
                    className="flex w-full items-center justify-between gap-3 bg-gray-50 px-4 py-3 text-left transition-colors hover:bg-gray-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary-500/60 dark:bg-gray-900/40 dark:hover:bg-white/[0.04]"
                  >
                    <div className="flex min-w-0 items-center gap-3">
                      {expandedGroups[groupKey] ? (
                        <ChevronDown className="h-4 w-4 flex-shrink-0 text-gray-400" />
                      ) : (
                        <ChevronRight className="h-4 w-4 flex-shrink-0 text-gray-400" />
                      )}
                      <span className="text-sm font-semibold text-gray-900 dark:text-gray-100">{group.label}</span>
                      <span className="text-sm text-gray-500 dark:text-gray-400">
                        ({group.subscriptions.length} notification{group.subscriptions.length === 1 ? '' : 's'})
                      </span>
                    </div>
                    {group.studyId && (
                      <Link
                        to={group.siteId 
                          ? `/studies/${group.studyId}/sites/${group.siteId}` 
                          : `/studies/${group.studyId}?tab=notifications`
                        }
                        onClick={(e) => e.stopPropagation()}
                        className="link inline-flex flex-shrink-0 items-center gap-1 text-sm"
                      >
                        View
                        <ArrowRight className="h-3.5 w-3.5" />
                      </Link>
                    )}
                  </button>

                  {/* Group Content */}
                  {expandedGroups[groupKey] && (
                    <div className="divide-y divide-gray-100 dark:divide-gray-700">
                      {group.subscriptions.map((sub) => {
                        const EventIcon = getEventIcon(sub.event_type_key);
                        return (
                        <motion.div
                          key={sub.id}
                          initial={{ opacity: 0 }}
                          animate={{ opacity: 1 }}
                          transition={{ duration: 0.2 }}
                          className="px-4 py-3 transition-colors hover:bg-gray-50/80 dark:hover:bg-white/[0.03]"
                        >
                          <div className="flex items-center justify-between gap-3">
                            <div className="flex min-w-0 flex-1 items-center gap-3">
                              <span className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg bg-gray-100 text-gray-500 dark:bg-white/[0.06] dark:text-gray-400">
                                <EventIcon className="h-4 w-4" />
                              </span>
                              <div className="min-w-0 flex-1">
                                <div className="text-sm font-medium text-gray-900 dark:text-gray-100">
                                  {getEventDisplayName(sub.event_type_key)}
                                </div>
                                <div className="mt-1 flex flex-wrap items-center gap-1.5">
                                  {sub.in_app_enabled && <span className="badge badge-brand">In-App</span>}
                                  {sub.email_enabled && <span className="badge badge-info">Email</span>}
                                </div>
                              </div>
                            </div>

                            <div className="flex flex-shrink-0 items-center gap-2">
                              {/* Toggle */}
                              <button
                                onClick={() => toggleSubscription(sub.id, sub.enabled)}
                                className={`relative inline-flex h-6 w-11 flex-shrink-0 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 dark:focus:ring-offset-gray-800 ${
                                  sub.enabled ? 'bg-primary-600' : 'bg-gray-200 dark:bg-gray-600'
                                }`}
                              >
                                <span
                                  className={`inline-block h-4 w-4 transform rounded-full bg-white shadow-sm transition-transform dark:bg-white ${
                                    sub.enabled ? 'translate-x-6' : 'translate-x-1'
                                  }`}
                                />
                              </button>

                              {/* Delete */}
                              <button
                                onClick={() => deleteSubscription(sub.id)}
                                className="flex h-8 w-8 items-center justify-center rounded-md text-gray-400 transition-colors hover:bg-danger-50 hover:text-danger-600 dark:hover:bg-danger-500/10 dark:hover:text-danger-400"
                                title="Remove notification"
                              >
                                <Trash2 className="h-4 w-4" />
                              </button>
                            </div>
                          </div>
                        </motion.div>
                        );
                      })}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className={embedded ? "mt-6 rounded-lg border border-gray-200/70 bg-gray-50 px-4 py-3 dark:border-white/[0.07] dark:bg-white/[0.03]" : "border-t border-gray-200 bg-gray-50 px-6 py-4 dark:border-gray-700 dark:bg-gray-900/40"}>
          <div className="flex items-start gap-2.5">
            <Lightbulb className="mt-0.5 h-4 w-4 flex-shrink-0 text-gray-400 dark:text-gray-500" />
            <div className="flex-1">
              <p className="mb-1 text-sm font-medium text-gray-700 dark:text-gray-200">How to Add Notifications</p>
              <p className="text-xs leading-relaxed text-gray-600 dark:text-gray-400">
                Visit any <Link to="/studies" className="link">study page</Link> and 
                click the <strong className="font-semibold text-gray-700 dark:text-gray-300">Notifications</strong> tab to enable study-specific alerts. You can also enable 
                site-specific notifications from individual site pages.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SimpleNotificationPreferences;
